import { FormSection } from 'redux-form';
import { Typography } from '@optimus-web/core';
import { lt } from '@mews/framework';
import {
    RateGroupOptions,
    SettlementRuleAction,
    SettlementRuleTrigger,
    SettlementRuleType,
} from '@mews-commander/data-access';
import { FieldsContainer } from '@mews-commander/ui';
import {
    ActorTimestampField,
    CurrentEnterpriseCurrencySelectField,
    DurationInputField,
    EnumSelectField,
    InputField,
    LocalizedInputField,
    LocalizedTextAreaField,
    RateGroupNumberInputField,
} from '@mews-commander/legacy-form';
import { SettlementValueField } from './SettlementValueField/SettlementValueField';
import {
    RateGroupFormSchemaKeys,
    SettlementRuleFormSchemaKeys,
    SettlementRuleSectionPrefix,
} from './RateGroupFormSchema';
import { SettlementExtentField } from './SettlementExtendField';

export interface RateGroupFormProps {
    defaultLanguageCode: string;
    serviceId: string;
    isEditForm?: boolean;
}

const SettlementRuleFormSection = () => (
    <>
        <EnumSelectField
            name={SettlementRuleFormSchemaKeys.action}
            label={lt('SettlementActionLabel')}
            bottomCaption={lt('SettlementActionHint')}
            enumClass={SettlementRuleAction}
            enumPrefix="SettlementRuleAction"
            required
        />
        <EnumSelectField
            name={SettlementRuleFormSchemaKeys.type}
            label={lt('SettlementTypeLabel')}
            bottomCaption={lt('SettlementTypeHint')}
            enumClass={SettlementRuleType}
            enumPrefix="SettlementRuleType"
            required
        />
        <EnumSelectField
            name={SettlementRuleFormSchemaKeys.trigger}
            label={lt('SettlementTriggerLabel')}
            bottomCaption={lt('SettlementTriggerHint')}
            enumClass={SettlementRuleTrigger}
            enumPrefix="SettlementRuleTrigger"
            required
        />
        <DurationInputField
            name={SettlementRuleFormSchemaKeys.offset}
            label={lt('SettlementOffsetLabel')}
            helper={lt('SettlementOffsetHint')}
            units={{ days: true, hours: true, minutes: true }}
        />
        <SettlementValueField name={SettlementRuleFormSchemaKeys.value} />
    </>
);

export const RateGroupForm = ({ defaultLanguageCode, serviceId, isEditForm = false }: RateGroupFormProps) => (
    <FieldsContainer>
        <Typography textStyle="titleSmall" variant="secondary" as="div">
            {lt('GeneralSettings')}
        </Typography>
        <LocalizedInputField
            name={RateGroupFormSchemaKeys.names}
            label={lt('Name')}
            requiredFields={[defaultLanguageCode]}
        />
        <LocalizedInputField name={RateGroupFormSchemaKeys.shortNames} label={lt('ShortName')} />
        <LocalizedTextAreaField name={RateGroupFormSchemaKeys.descriptions} label={lt('Description')} />
        <RateGroupNumberInputField name={RateGroupFormSchemaKeys.ordering} label={lt('Ordering')} />
        <InputField
            name={RateGroupFormSchemaKeys.externalIdentifier}
            label={lt('RateGroupKey')}
            bottomCaption={lt('ExternalIdentifierHint')}
        />
        <Typography textStyle="titleSmall" variant="secondary" as="div">
            {lt('GeneralSettings')}
        </Typography>
        <RateGroupNumberInputField
            name={RateGroupFormSchemaKeys.settlementMaximumTimeUnits}
            label={lt('PaymentMaximumTime')}
            helper={lt('PaymentMaximumTimeUnitsHint')}
        />
        <SettlementExtentField serviceId={serviceId} />
        <EnumSelectField
            name={RateGroupFormSchemaKeys.options}
            label={lt('Options')}
            bottomCaption={lt('RateGroupOptionsHint')}
            enumClass={RateGroupOptions}
            enumPrefix="RateGroupOptions"
            multiselect
        />
        <CurrentEnterpriseCurrencySelectField
            name={RateGroupFormSchemaKeys.settlementCurrencyCode}
            label={lt('Currency')}
            disabled={isEditForm}
            readOnly={isEditForm}
            required
        />
        <Typography textStyle="titleSmall" variant="secondary" as="div">
            {lt('SettlementRuleFirstConfiguration')}
        </Typography>
        <FormSection name={SettlementRuleSectionPrefix}>
            <SettlementRuleFormSection />
        </FormSection>
        {isEditForm && (
            <>
                <ActorTimestampField name={RateGroupFormSchemaKeys.created} label={lt('Created')} />
                <ActorTimestampField name={RateGroupFormSchemaKeys.updated} label={lt('Updated')} />
            </>
        )}
    </FieldsContainer>
);
